import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { 
  FileText, 
  Plus, 
  Edit2, 
  Trash2, 
  GripVertical,
  Loader2,
  Copy,
  Star,
  Type,
  AlignLeft,
  Hash,
  DollarSign,
  ToggleLeft,
  CircleDot,
  Eye,
  CheckCircle,
  X
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
axios.defaults.withCredentials = true;

const FIELD_TYPES = [
  { value: "rich_text", label: "Rich Text", description: "Formatted text with bold, colors, etc.", Icon: FileText },
  { value: "text", label: "Text", description: "Single line text input", Icon: Type },
  { value: "paragraph", label: "Paragraph", description: "Multi-line text area", Icon: AlignLeft },
  { value: "number", label: "Number", description: "Numeric input only", Icon: Hash },
  { value: "currency", label: "Currency", description: "Money/currency input", Icon: DollarSign },
  { value: "true_false", label: "True/False", description: "Boolean checkbox", Icon: ToggleLeft },
  { value: "yes_no", label: "Yes/No", description: "Yes or No selection", Icon: CircleDot },
];

const DEFAULT_FIELD = {
  id: "",
  label: "",
  description: "",
  field_type: "rich_text",
  required: false,
  has_attachments: false,
  quick_tags: []
};

export default function FormsPage() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [editingForm, setEditingForm] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Preview dialog state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewForm, setPreviewForm] = useState(null);
  const [previewData, setPreviewData] = useState({});
  
  // Form editor state
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formFields, setFormFields] = useState([]);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/forms`);
      setForms(response.data);
    } catch (error) {
      toast.error("Failed to load forms");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openPreviewDialog = (form) => {
    setPreviewForm(form);
    setPreviewData({});
    setPreviewDialogOpen(true);
  };

  const updatePreviewField = (fieldId, value) => {
    setPreviewData(prev => ({ ...prev, [fieldId]: value }));
  };

  const openCreateDialog = () => {
    setEditingForm(null);
    setFormName("");
    setFormDescription("");
    setFormFields([{ ...DEFAULT_FIELD, id: `field_${Date.now()}` }]);
    setIsActive(true);
    setDialogOpen(true);
  };

  const openEditDialog = (form) => {
    setEditingForm(form);
    setFormName(form.name);
    setFormDescription(form.description || "");
    setFormFields(form.fields || []);
    setIsActive(form.is_active !== false);
    setDialogOpen(true);
  };

  const openDeleteDialog = (form) => {
    setFormToDelete(form);
    setDeleteDialogOpen(true);
  };

  const handleDuplicateForm = async (form) => {
    try {
      const newFormData = {
        name: `${form.name} (Copy)`,
        description: form.description || "",
        fields: form.fields.map(f => ({ ...f, id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` })),
        is_active: true
      };
      
      await axios.post(`${API}/forms`, newFormData);
      toast.success("Form duplicated successfully");
      fetchForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to duplicate form");
    }
  };

  const addField = () => {
    setFormFields([
      ...formFields,
      { ...DEFAULT_FIELD, id: `field_${Date.now()}` }
    ]);
  };

  const updateField = (index, updates) => {
    const newFields = [...formFields];
    newFields[index] = { ...newFields[index], ...updates };
    setFormFields(newFields);
  };

  const removeField = (index) => {
    if (formFields.length <= 1) {
      toast.error("Form must have at least one field");
      return;
    }
    setFormFields(formFields.filter((_, i) => i !== index));
  };

  const moveField = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= formFields.length) return;
    
    const newFields = [...formFields];
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    setFormFields(newFields);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error("Form name is required");
      return;
    }
    
    if (formFields.length === 0) {
      toast.error("Form must have at least one field");
      return;
    }
    
    // Validate fields
    for (const field of formFields) {
      if (!field.label.trim()) {
        toast.error("All fields must have a label");
        return;
      }
    }
    
    setSaving(true);
    try {
      const formData = {
        name: formName.trim(),
        description: formDescription.trim(),
        fields: formFields.map(f => ({
          ...f,
          id: f.id || `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        })),
        is_active: isActive
      };
      
      if (editingForm) {
        await axios.put(`${API}/forms/${editingForm.id}`, formData);
        toast.success("Form updated successfully");
      } else {
        await axios.post(`${API}/forms`, formData);
        toast.success("Form created successfully");
      }
      
      setDialogOpen(false);
      fetchForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save form");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!formToDelete) return;
    
    try {
      await axios.delete(`${API}/forms/${formToDelete.id}`);
      toast.success("Form deleted successfully");
      setDeleteDialogOpen(false);
      setFormToDelete(null);
      fetchForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete form");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Forms</h1>
          <p className="text-slate-500 mt-1">Manage daily log forms and assign them to users</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="create-form-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Form
        </Button>
      </div>

      {/* Forms List */}
      <div className="grid gap-4">
        {forms.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No forms created yet</p>
              <Button onClick={openCreateDialog} variant="outline" className="mt-4">
                Create your first form
              </Button>
            </CardContent>
          </Card>
        ) : (
          forms.map((form) => (
            <Card key={form.id} className={!form.is_active ? "opacity-60" : ""}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${form.is_default ? "bg-amber-100" : "bg-blue-100"}`}>
                      <FileText className={`h-5 w-5 ${form.is_default ? "text-amber-600" : "text-blue-600"}`} />
                    </div>
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {form.name}
                        {form.is_default && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                            <Star className="h-3 w-3 mr-1" />
                            Default
                          </Badge>
                        )}
                        {!form.is_active && (
                          <Badge variant="outline" className="bg-slate-100 text-slate-600">
                            Inactive
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {form.description || "No description"}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openPreviewDialog(form)}
                      title="Preview form"
                      data-testid={`preview-form-${form.id}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDuplicateForm(form)}
                      title="Duplicate form"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(form)}
                      data-testid={`edit-form-${form.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    {!form.is_default && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteDialog(form)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-form-${form.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {form.fields?.map((field) => {
                    const fieldTypeInfo = FIELD_TYPES.find(ft => ft.value === field.field_type) || FIELD_TYPES[0];
                    return (
                      <Badge key={field.id} variant="secondary" className="font-normal flex items-center gap-1">
                        <fieldTypeInfo.Icon className="h-3 w-3" />
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </Badge>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-400 mt-3">
                  {form.fields?.length || 0} field(s) • Created {new Date(form.created_at).toLocaleDateString()}
                </p>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingForm ? "Edit Form" : "Create Form"}</DialogTitle>
            <DialogDescription>
              {editingForm ? "Update the form fields and settings" : "Create a new daily log form with custom fields"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Form Name & Description */}
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="formName">Form Name <span className="text-red-500">*</span></Label>
                <Input
                  id="formName"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g., Daily Log, Weekly Report"
                  disabled={editingForm?.is_default}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="formDescription">Description</Label>
                <Textarea
                  id="formDescription"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Brief description of this form"
                  rows={2}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="isActive"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
                <Label htmlFor="isActive">Form is active</Label>
              </div>
            </div>

            {/* Fields */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Form Fields</Label>
                <Button type="button" variant="outline" size="sm" onClick={addField}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Field
                </Button>
              </div>

              <div className="space-y-4">
                {formFields.map((field, index) => {
                  const fieldTypeInfo = FIELD_TYPES.find(ft => ft.value === field.field_type) || FIELD_TYPES[0];
                  const FieldIcon = fieldTypeInfo.Icon;
                  
                  return (
                  <Card key={field.id} className="border-slate-200">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col gap-1 pt-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => moveField(index, -1)}
                            disabled={index === 0}
                          >
                            ▲
                          </Button>
                          <GripVertical className="h-4 w-4 text-slate-400 mx-auto" />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => moveField(index, 1)}
                            disabled={index === formFields.length - 1}
                          >
                            ▼
                          </Button>
                        </div>
                        
                        <div className="flex-1 grid gap-3">
                          {/* Field Type Selector */}
                          <div className="space-y-1">
                            <Label className="text-xs">Field Type</Label>
                            <Select 
                              value={field.field_type} 
                              onValueChange={(val) => updateField(index, { 
                                field_type: val,
                                has_attachments: val === "rich_text" || val === "paragraph"
                              })}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue>
                                  <div className="flex items-center gap-2">
                                    <FieldIcon className="h-4 w-4 text-slate-500" />
                                    {fieldTypeInfo.label}
                                  </div>
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {FIELD_TYPES.map((ft) => (
                                  <SelectItem key={ft.value} value={ft.value}>
                                    <div className="flex items-center gap-2">
                                      <ft.Icon className="h-4 w-4 text-slate-500" />
                                      <div>
                                        <span className="font-medium">{ft.label}</span>
                                        <span className="text-xs text-slate-400 ml-2">{ft.description}</span>
                                      </div>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Field Label <span className="text-red-500">*</span></Label>
                              <Input
                                value={field.label}
                                onChange={(e) => updateField(index, { label: e.target.value })}
                                placeholder="e.g., Tasks Today"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Field ID</Label>
                              <Input
                                value={field.id}
                                onChange={(e) => updateField(index, { id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                                placeholder="e.g., tasks_today"
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Description</Label>
                            <Input
                              value={field.description}
                              onChange={(e) => updateField(index, { description: e.target.value })}
                              placeholder="Help text for users"
                            />
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id={`required-${index}`}
                                checked={field.required}
                                onCheckedChange={(checked) => updateField(index, { required: checked })}
                              />
                              <Label htmlFor={`required-${index}`} className="text-sm">Required</Label>
                            </div>
                            {(field.field_type === "rich_text" || field.field_type === "paragraph") && (
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id={`attachments-${index}`}
                                  checked={field.has_attachments}
                                  onCheckedChange={(checked) => updateField(index, { has_attachments: checked })}
                                />
                                <Label htmlFor={`attachments-${index}`} className="text-sm">Allow Attachments</Label>
                              </div>
                            )}
                          </div>
                        </div>

                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeField(index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          disabled={formFields.length <= 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : editingForm ? (
                "Update Form"
              ) : (
                "Create Form"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Form</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{formToDelete?.name}&quot;? This action cannot be undone.
              Users assigned to this form will need to be reassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Form Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Preview: {previewForm?.name}
            </DialogTitle>
            <DialogDescription>
              This is how the form will appear to users. Fields are interactive for demonstration.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {previewForm?.fields?.map((field) => {
              const fieldTypeInfo = FIELD_TYPES.find(ft => ft.value === field.field_type) || FIELD_TYPES[0];
              const FieldIcon = fieldTypeInfo.Icon;
              
              return (
                <div key={field.id} className="space-y-2" data-testid={`preview-field-${field.id}`}>
                  <div className="flex items-center gap-2">
                    <FieldIcon className="h-4 w-4 text-slate-400" />
                    <Label className="text-sm font-medium">
                      {field.label}
                      {field.required ? (
                        <span className="text-red-500 ml-1">*</span>
                      ) : (
                        <span className="text-xs text-slate-400 ml-1">(Optional)</span>
                      )}
                    </Label>
                  </div>
                  {field.description && (
                    <p className="text-xs text-slate-500">{field.description}</p>
                  )}
                  
                  {/* Render field based on type */}
                  {field.field_type === "text" && (
                    <Input
                      value={previewData[field.id] || ""}
                      onChange={(e) => updatePreviewField(field.id, e.target.value)}
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                    />
                  )}
                  
                  {field.field_type === "paragraph" && (
                    <Textarea
                      value={previewData[field.id] || ""}
                      onChange={(e) => updatePreviewField(field.id, e.target.value)}
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                      rows={4}
                      className="resize-none"
                    />
                  )}
                  
                  {field.field_type === "rich_text" && (
                    <div className="border rounded-lg p-3 min-h-[120px] bg-slate-50">
                      <Textarea
                        value={previewData[field.id] || ""}
                        onChange={(e) => updatePreviewField(field.id, e.target.value)}
                        placeholder="Rich text editor (simplified for preview)"
                        rows={4}
                        className="resize-none border-0 bg-transparent focus-visible:ring-0 p-0"
                      />
                      <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        Full editor with formatting available in actual form
                      </p>
                    </div>
                  )}
                  
                  {field.field_type === "number" && (
                    <Input
                      type="number"
                      value={previewData[field.id] || ""}
                      onChange={(e) => updatePreviewField(field.id, e.target.value)}
                      placeholder="Enter a number"
                    />
                  )}
                  
                  {field.field_type === "currency" && (
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={previewData[field.id] || ""}
                        onChange={(e) => updatePreviewField(field.id, e.target.value)}
                        placeholder="0.00"
                        className="pl-7"
                      />
                    </div>
                  )}
                  
                  {field.field_type === "true_false" && (
                    <div className="flex items-center gap-3 py-2">
                      <Switch
                        checked={previewData[field.id] === true}
                        onCheckedChange={(checked) => updatePreviewField(field.id, checked)}
                      />
                      <span className="text-sm text-slate-600">
                        {previewData[field.id] === true ? "True" : "False"}
                      </span>
                    </div>
                  )}
                  
                  {field.field_type === "yes_no" && (
                    <div className="flex gap-3">
                      <label
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                          previewData[field.id] === "yes" 
                            ? "border-green-500 bg-green-50" 
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name={`preview-${field.id}`}
                          value="yes"
                          checked={previewData[field.id] === "yes"}
                          onChange={() => updatePreviewField(field.id, "yes")}
                          className="sr-only"
                        />
                        <CheckCircle className={`h-4 w-4 ${previewData[field.id] === "yes" ? "text-green-600" : "text-slate-400"}`} />
                        <span className={`text-sm font-medium ${previewData[field.id] === "yes" ? "text-green-700" : "text-slate-600"}`}>Yes</span>
                      </label>
                      <label
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                          previewData[field.id] === "no" 
                            ? "border-red-500 bg-red-50" 
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name={`preview-${field.id}`}
                          value="no"
                          checked={previewData[field.id] === "no"}
                          onChange={() => updatePreviewField(field.id, "no")}
                          className="sr-only"
                        />
                        <X className={`h-4 w-4 ${previewData[field.id] === "no" ? "text-red-600" : "text-slate-400"}`} />
                        <span className={`text-sm font-medium ${previewData[field.id] === "no" ? "text-red-700" : "text-slate-600"}`}>No</span>
                      </label>
                    </div>
                  )}
                  
                  {/* Show attachment badge if enabled */}
                  {field.has_attachments && (
                    <p className="text-xs text-blue-600 flex items-center gap-1">
                      <Plus className="h-3 w-3" />
                      File attachments enabled
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
              Close Preview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
